import React from 'react';
import styled from 'styled-components/macro';

import modularScale from 'styles/helpers/modular-scale';

import { LinkWithSearchParams as Link } from 'components/base/Link/Link';
import CompareListItem from './CompareListItem/CompareListItem';
import EntityItem from 'components/base/EntityItem/EntityItem';
import {
  StyledArtwork,
  ArtworkWrapper,
} from 'components/base/EntityItem/EntityItem.style';

import { ReactComponent as IconClose } from 'icons/icon-close.svg';
import { ReactComponent as IconTryAgain } from 'icons/icon-try-again.svg';
import { IEntitiesById, IReduxEntity } from '../EntityCompareHeader';
import { ContextTopics } from 'context/entityContext';

const StyledLink = styled(Link)`
  color: white;
  cursor: default;
  margin-left: ${({ theme }): string => theme.spacing.xsmall};
  opacity: 0.75;

  svg {
    display: block;
  }

  &:hover {
    opacity: 1;
  }

  @media screen and (min-width: 30em) {
    margin-left: ${({ theme }): string => theme.spacing.small};
  }
`;

const StyledEntityItem = styled(EntityItem)`
  h3,
  p {
    color: inherit;
  }
`;

const ErrorMessage = styled('div')`
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
`;

const RetryButton = styled('button')`
  appearance: none;
  position: relative;
  margin: 0;
  padding: ${({ theme }): string =>
    `${theme.spacing.xsmall} ${theme.spacing.medium}`};
  color: ${({ theme }): string => theme.DeepBlue};
  font-size: ${modularScale(-2)};
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1;
  background: white;
  border-radius: 0.2em;
  transition: ${({ theme }): string =>
    `all ${theme.transitionDuration} ${theme.timingFunction}`};

  svg {
    vertical-align: middle;
    margin-right: ${({ theme }): string => theme.spacing.small};
    margin-top: -2px;
  }
`;

interface ICompareListProps {
  entityIds: string[];
  /** The current EntityType */
  entityType: string;
  /** The EntitiesByID object from Redux in which the EntityMetadat is stored */
  entitiesById: IEntitiesById;
  /** The current topic that is being browsed */
  topic: ContextTopics;
  /** Method to reload an individual entity based on id */
  reloadEntity: (...args: any[]) => any;
}

/** The List of entities that are currently in compare */
const CompareList: React.FC<ICompareListProps> = ({
  entityIds,
  entityType,
  entitiesById,
  topic,
  reloadEntity,
}) => (
  <ul>
    {entityIds.map((id: string, index: number) => {
      if (!entitiesById[id]) {
        return null;
      }

      const entityData: IReduxEntity = entitiesById[id];
      const { entity, loading, error } = entityData;

      const removalLink =
        entityIds.length > 1
          ? `/compare/entity/${entityType}/${entityIds.filter(
              (loopId) => id !== loopId
            )}/${topic.join('/')}`
          : `/entity/${entityType}/${id}/${topic.join('/')}`;

      return (
        <li key={id}>
          <CompareListItem index={index} loading={loading} error={error}>
            {(!entity || loading) && !error && (
              <ArtworkWrapper>
                <StyledArtwork entityType={entityType} />
              </ArtworkWrapper>
            )}
            {entity && !loading && (
              <StyledEntityItem
                entityId={entity.id}
                entityType={entity.type}
                displayName={entity.displayName}
                primaryArtist={entity.primaryArtist}
                thumbnailUrl={entity.thumbnailUrl}
                releaseDate={entity.releaseDate}
                isGroup={entity.is_group}
                parentEntityId={entity.parent_group_id}
                groupChildCount={entity.group_child_count}
                releaseCount={entity.grouped_album_count}
                artistCount={entity.artist_count}
                trackCount={entity.grouped_track_count}
                entityData={entity.data}
                artworkHasBackground={false}
                releaseType={entity?.productType}
                releaseTypeColor="#fff"
              />
            )}
            {!entity && error && (
              <ErrorMessage>
                <span>Something went wrong</span>
                <RetryButton onClick={(): void => reloadEntity(id)}>
                  Retry <IconTryAgain />
                </RetryButton>
              </ErrorMessage>
            )}
            {(index > 0 || entityIds.length > 1) && (
              <StyledLink to={removalLink}>
                <IconClose />
              </StyledLink>
            )}
          </CompareListItem>
        </li>
      );
    })}
  </ul>
);

export default CompareList;
